import React, { useState, useEffect } from 'react';
import { adminAPI } from '../../lib/api';
import { Flag, Check, X, Trash2, ChevronLeft, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';

export default function AdminReports() {
  const [reports, setReports] = useState([]);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(0);
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState('');
  const [loading, setLoading] = useState(true);

  const fetchReports = async () => {
    setLoading(true);
    try {
      const params = { page, limit: 15 };
      if (statusFilter) params.status = statusFilter;
      const { data } = await adminAPI.getReports(params);
      setReports(data.reports); setTotal(data.total); setPages(data.pages);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchReports(); }, [page, statusFilter]);

  const handleAction = async (reportId, status, action) => {
    try {
      await adminAPI.updateReport(reportId, { status, action });
      toast.success(action === 'remove_listing' ? 'Listing removed & report resolved' : 'Report updated');
      fetchReports();
    } catch (err) { toast.error('Failed to update report'); }
  };

  return (
    <div data-testid="admin-reports">
      <div className="mb-6">
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-zinc-900">Reports</h1>
        <p className="text-sm text-zinc-500 mt-1">{total} total reports</p>
      </div>

      <div className="flex gap-2 mb-4">
        <select value={statusFilter} onChange={e => { setStatusFilter(e.target.value); setPage(1); }}
          className="border border-zinc-200 rounded-lg px-3 py-2.5 text-sm bg-white" data-testid="admin-reports-filter">
          <option value="">All Reports</option>
          <option value="pending">Pending</option>
          <option value="reviewed">Reviewed</option>
          <option value="dismissed">Dismissed</option>
        </select>
      </div>

      <div className="space-y-3">
        {loading ? (
          [...Array(3)].map((_, i) => <div key={i} className="bg-white rounded-lg border border-zinc-200 p-5 animate-pulse"><div className="h-4 bg-zinc-100 rounded w-1/3 mb-3" /><div className="h-4 bg-zinc-100 rounded w-2/3" /></div>)
        ) : reports.length === 0 ? (
          <div className="bg-white rounded-lg border border-zinc-200 p-8 text-center">
            <Flag className="w-10 h-10 text-zinc-200 mx-auto mb-3" />
            <p className="text-zinc-500">No reports found</p>
          </div>
        ) : reports.map(r => (
          <div key={r.report_id} className="bg-white rounded-lg border border-zinc-200 p-5" data-testid={`report-${r.report_id}`}>
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${r.status === 'pending' ? 'bg-amber-100 text-amber-700' : r.status === 'reviewed' ? 'bg-emerald-100 text-emerald-700' : 'bg-zinc-100 text-zinc-600'}`}>
                    {r.status}
                  </span>
                  <span className="text-xs text-zinc-400">{new Date(r.created_at).toLocaleString()}</span>
                </div>
                <p className="font-semibold text-zinc-900 mb-1">Reason: <span className="capitalize">{r.reason}</span></p>
                {r.details && <p className="text-sm text-zinc-600 mb-2">{r.details}</p>}
                <div className="flex flex-wrap gap-4 text-xs text-zinc-500">
                  <span>Listing: <strong className="text-zinc-700">{r.listing_info?.title || r.listing_id}</strong></span>
                  <span>Seller: <strong className="text-zinc-700">{r.listing_info?.seller_name || 'Unknown'}</strong></span>
                  <span>Reporter: <strong className="text-zinc-700">{r.reporter_info?.name || 'Unknown'}</strong> ({r.reporter_info?.email})</span>
                </div>
              </div>
              {r.status === 'pending' && (
                <div className="flex gap-1 flex-shrink-0">
                  <button onClick={() => handleAction(r.report_id, 'reviewed', 'remove_listing')}
                    className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-red-50 text-red-600 text-xs font-bold hover:bg-red-100 transition-colors"
                    data-testid={`report-remove-${r.report_id}`}
                  ><Trash2 className="w-3 h-3" /> Remove Listing</button>
                  <button onClick={() => handleAction(r.report_id, 'dismissed', null)}
                    className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-zinc-100 text-zinc-600 text-xs font-bold hover:bg-zinc-200 transition-colors"
                    data-testid={`report-dismiss-${r.report_id}`}
                  ><X className="w-3 h-3" /> Dismiss</button>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {pages > 1 && (
        <div className="flex items-center justify-between mt-4">
          <p className="text-xs text-zinc-500">Page {page} of {pages}</p>
          <div className="flex gap-1">
            <button onClick={() => setPage(Math.max(1, page - 1))} disabled={page === 1} className="p-1.5 rounded-md border border-zinc-200 disabled:opacity-50"><ChevronLeft className="w-4 h-4" /></button>
            <button onClick={() => setPage(Math.min(pages, page + 1))} disabled={page === pages} className="p-1.5 rounded-md border border-zinc-200 disabled:opacity-50"><ChevronRight className="w-4 h-4" /></button>
          </div>
        </div>
      )}
    </div>
  );
}
